<template>
  <div :class="{ [bem.is('vertical')]: vertical, [bem.b()]: true }">
    <div
      ref="bar"
      :class="bem.m('bar')"
      :style="{ background }"
      @click="handleClick"
    />
    <div
      ref="thumb"
      :class="bem.m('thumb')"
      :style="{ left: thumbLeft + 'px', top: thumbTop + 'px' }"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Nullable } from '@ll-plus/utils'
import { createNamespace } from '@ll-plus/utils'
import { getCurrentInstance, onMounted, ref, shallowRef, watch } from 'vue'
import { alphaSliderProps } from '../config'
import draggable from '../config/draggable'

// Config
defineOptions({ name: 'AlphaSlider' })
const props = defineProps(alphaSliderProps)
const bem = createNamespace('alpha-slider')
const instance = getCurrentInstance()

//Data
const bar = shallowRef<Nullable<HTMLElement>>(null)
const thumb = shallowRef<Nullable<HTMLElement>>(null)
const thumbLeft = ref<number>(0)
const thumbTop = ref<number>(0)
const background = ref<string>('')
// Methods

const handleClick = (event: MouseEvent) => {
  const target = event.target

  if (target !== thumb.value) {
    handleDrag(event)
  }
}

const handleDrag = (event: MouseEvent) => {
  const ele = instance?.vnode.el as HTMLElement
  const rect = ele.getBoundingClientRect()

  if (!props.vertical) {
    let left = event.clientX - rect.left
    left = Math.max((thumb.value as HTMLElement).offsetWidth / 2, left)
    left = Math.min(
      left,
      rect.width - (thumb.value as HTMLElement).offsetWidth / 2
    )

    props.color?.set(
      'alpha',
      Math.round(
        ((left - (thumb.value as HTMLElement).offsetWidth / 2) /
          (rect.width - (thumb.value as HTMLElement).offsetWidth)) *
          100
      )
    )
  } else {
    let top = event.clientY - rect.top
    top = Math.max((thumb.value as HTMLElement).offsetHeight / 2, top)
    top = Math.min(
      top,
      rect.height - (thumb.value as HTMLElement).offsetHeight / 2
    )

    props.color?.set(
      'alpha',
      Math.round(
        ((top - (thumb.value as HTMLElement).offsetHeight / 2) /
          (rect.height - (thumb.value as HTMLElement).offsetHeight)) *
          100
      )
    )
  }
}
const update = () => {
  thumbLeft.value = getThumbLeft()
  thumbTop.value = getThumbTop()
  background.value = getBackground()
}
const getThumbLeft = (): number => {
  if (props.vertical) return 0
  const ele = instance?.vnode.el
  const alpha = props.color?.get('alpha')

  if (!ele) return 0
  return Math.round(
    (alpha * (ele.offsetWidth - (thumb.value as HTMLElement).offsetWidth / 2)) /
      100
  )
}
const getThumbTop = (): number => {
  const ele = instance?.vnode.el
  if (!props.vertical) return 0
  const alpha = props.color?.get('alpha')

  if (!ele) return 0
  return Math.round(
    (alpha *
      (ele.offsetHeight - (thumb.value as HTMLElement).offsetHeight / 2)) /
      100
  )
}
const getBackground = () => {
  if (props.color && props.color.value) {
    const { r, g, b } = props.color.toRgb()
    return `linear-gradient(to right, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 1) 100%)`
  }
  return ''
}

// Watch
watch(
  () => props.color?.get('alpha'),
  () => {
    update()
  }
)
watch(
  () => props.color?.value,
  () => {
    update()
  }
)

onMounted(() => {
  const dragConfig = {
    drag: (event: MouseEvent) => {
      handleDrag(event)
    },
    end: (event: MouseEvent) => {
      handleDrag(event)
    },
  }

  draggable(bar.value as HTMLElement, dragConfig)
  draggable(thumb.value as HTMLElement, dragConfig)
  update()
})

defineExpose({
  update,
})
</script>
